import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs';

import { errorMessageParser, ShowError } from '@ui/legacy-lib';

import { PaymentsFacade } from '../../core/services/payments.facade';
import { SmartDepositService } from '../../screens/payment/smart-deposit/smart-deposit.service';
import { notificationConfig } from '../../config/notification.config';
import {
  applySmartDeposit,
  applySmartDepositError,
  applySmartDepositSuccess,
  cancelSmartDeposit,
  cancelSmartDepositError,
  cancelSmartDepositLegitimation,
  cancelSmartDepositLegitimationError,
  cancelSmartDepositLegitimationSuccess,
  cancelSmartDepositSuccess,
  createCardRegistration,
  createCardRegistrationError,
  createCardRegistrationSuccess,
  deleteCreditCard,
  deleteCreditCardError,
  deleteCreditCardSuccess,
  fetchMandate,
  fetchMandateFail,
  fetchMandateSuccess,
  fetchPaymentBranding,
  fetchPaymentBrandingFail,
  fetchPaymentBrandingSuccess,
  fetchPaymentRequest,
  fetchPaymentRequestFail,
  fetchPaymentRequestSuccess,
  fetchSmartDeposit,
  fetchSmartDepositError,
  fetchSmartDepositSuccess,
  loadSmartDepositBranding,
  loadSmartDepositBrandingError,
  loadSmartDepositBrandingSuccess,
  loadSmartDepositDictionary,
  loadSmartDepositDictionaryError,
  loadSmartDepositDictionarySuccess,
  loadSmartDepositFee,
  loadSmartDepositFeeError,
  loadSmartDepositFeeSuccess,
  manualPayment,
  manualPaymentFail,
  manualPaymentSuccess,
  paymentExecution,
  paymentExecutionFail,
  paymentExecutionSuccess,
  startSmartDepositLegitimation,
  startSmartDepositLegitimationError,
  startSmartDepositLegitimationSuccess
} from './payments.actions';

@Injectable()
export class PaymentsEffects {
  private actions$ = inject(Actions);
  private facade = inject(PaymentsFacade);
  fetchPaymentRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchPaymentRequest),
      switchMap(({ token }) =>
        this.facade.fetchPaymentRequest(token).pipe(
          map(paymentRequest => fetchPaymentRequestSuccess({ paymentRequest })),
          catchError((error: Error) => [
            fetchPaymentRequestFail({ error }),
            new ShowError(notificationConfig.payments.fetchPaymentRequest.error)
          ])
        )
      )
    )
  );
  fetchMandate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchMandate),
      switchMap(({ token, mandateId }) =>
        this.facade.fetchMandate(token, mandateId).pipe(
          map(paymentMandate => fetchMandateSuccess({ paymentMandate })),
          catchError((error: Error) => [
            fetchMandateFail({ error }),
            new ShowError(notificationConfig.payments.fetchMandate.error)
          ])
        )
      )
    )
  );
  fetchPaymentBranding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchPaymentBranding),
      switchMap(({ token }) =>
        this.facade.fetchPaymentBranding(token).pipe(
          map(paymentBranding =>
            fetchPaymentBrandingSuccess({
              paymentBranding
            })
          ),
          catchError((error: Error) => [
            fetchPaymentBrandingFail({ error }),
            new ShowError(
              notificationConfig.payments.fetchPaymentBranding.error
            )
          ])
        )
      )
    )
  );
  manualPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(manualPayment),
      switchMap(({ input }) =>
        this.facade.manualPayment(input).pipe(
          map(() => manualPaymentSuccess()),
          catchError((error: Error) => [
            manualPaymentFail({ error }),
            new ShowError(notificationConfig.payments.manualPayment.error)
          ])
        )
      )
    )
  );
  paymentExecution$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentExecution),
      switchMap(({ input }) =>
        this.facade.paymentExecution(input).pipe(
          map(response => paymentExecutionSuccess({ response })),
          catchError((error: Error) => [
            paymentExecutionFail({ error }),
            new ShowError(notificationConfig.payments.paymentExecution.error)
          ])
        )
      )
    )
  );
  smartDepositFee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSmartDepositFee),
      switchMap(({ deposit }) =>
        this.facade.loadSmartDepositFee(deposit).pipe(
          map(smartDepositFee => loadSmartDepositFeeSuccess(smartDepositFee)),
          catchError((error: Error) => [
            loadSmartDepositFeeError({ error }),
            new ShowError(
              notificationConfig.smartDeposit.loadSmartDepositFee.error
            )
          ])
        )
      )
    )
  );
  fetchSmartDeposit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchSmartDeposit),
      switchMap(({ token }) =>
        this.facade.fetchSmartDeposit(token).pipe(
          map(smartDepositOverview =>
            fetchSmartDepositSuccess({ smartDepositOverview })
          ),
          catchError((error: Error) => [
            fetchSmartDepositError({ error }),
            new ShowError(
              notificationConfig.smartDeposit.fetchSmartDeposit.error
            )
          ])
        )
      )
    )
  );
  loadSmartDepositBranding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSmartDepositBranding),
      switchMap(({ token }) =>
        this.facade.loadSmartDepositBranding(token).pipe(
          map(smartDepositBranding =>
            loadSmartDepositBrandingSuccess({
              smartDepositBranding
            })
          ),
          catchError((error: Error) => [
            loadSmartDepositBrandingError({ error }),
            new ShowError(
              notificationConfig.smartDeposit.loadSmartDepositBranding.error
            )
          ])
        )
      )
    )
  );
  applySmartDeposit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(applySmartDeposit),
      switchMap(({ token, smartDepositApplicationPayload }) =>
        this.facade
          .applySmartDeposit(token, smartDepositApplicationPayload)
          .pipe(
            map(({ token }) => applySmartDepositSuccess({ token })),
            catchError(error => [
              applySmartDepositError({ error }),
              new ShowError(
                notificationConfig.smartDeposit.applySmartDeposit.error
              )
            ])
          )
      )
    )
  );
  cancelSmartDeposit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelSmartDeposit),
      switchMap(({ token }) =>
        this.facade.cancelSmartDeposit(token).pipe(
          map(() => cancelSmartDepositSuccess()),
          catchError(error => [
            cancelSmartDepositError({ error }),
            new ShowError(
              notificationConfig.smartDeposit.cancelSmartDeposit.error
            )
          ])
        )
      )
    )
  );
  startSmartDepositLegitimation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSmartDepositLegitimation),
      switchMap(({ token, input }) =>
        this.facade.startSmartDepositLegitimation(token, input).pipe(
          map(() => startSmartDepositLegitimationSuccess()),
          catchError(error => [
            startSmartDepositLegitimationError({ error }),
            new ShowError(
              notificationConfig.smartDeposit.startSmartDepositLegitimation
                .error
            )
          ])
        )
      )
    )
  );
  cancelSmartDepositLegitimation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelSmartDepositLegitimation),
      switchMap(({ token }) =>
        this.facade.cancelSmartDepositLegitimation(token).pipe(
          map(() => cancelSmartDepositLegitimationSuccess()),
          catchError(error => [
            cancelSmartDepositLegitimationError({ error }),
            new ShowError(
              notificationConfig.smartDeposit.cancelSmartDepositLegitimation
                .error
            )
          ])
        )
      )
    )
  );
  private smartDepositService = inject(SmartDepositService);
  smartDepositDictionary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSmartDepositDictionary),
      switchMap(() =>
        this.facade.loadSmartDepositDictionary().pipe(
          map(dictionary =>
            this.smartDepositService.addTranslateKeysToDictionary(dictionary)
          ),
          map(smartDepositDictionary =>
            loadSmartDepositDictionarySuccess({
              smartDepositDictionary
            })
          ),
          catchError((error: Error) => [
            loadSmartDepositDictionaryError({ error }),
            new ShowError(
              notificationConfig.smartDeposit.loadSmartDepositDictionary.error
            )
          ])
        )
      )
    )
  );

  createCardRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createCardRegistration),
      switchMap(({ token, cardType }) =>
        this.facade.createCardRegistration(token, cardType).pipe(
          map(cardRegistration =>
            createCardRegistrationSuccess({
              cardRegistration
            })
          ),
          catchError(error => [
            createCardRegistrationError({ error }),
            new ShowError(errorMessageParser(error))
          ])
        )
      )
    )
  );

  deleteCreditCard = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCreditCard),
      switchMap(({ token, creditCardId }) =>
        this.facade.deleteCreditCard(token, creditCardId).pipe(
          map(() => deleteCreditCardSuccess()),
          catchError(error => [
            deleteCreditCardError({ error }),
            new ShowError(errorMessageParser(error))
          ])
        )
      )
    )
  );
}
